import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../services/api';

interface LogItem {
  id: string;
  appId: string;
  appKey: string;
  appName: string;
  direction: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  records: number | null;
  durationMs: number | null;
  message: string | null;
}

interface LogsResponse {
  items: LogItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface AppLite {
  id: string;
  appKey: string;
  appName: string;
}

export default function LogsPage() {
  const [page, setPage] = useState(1);
  const [appId, setAppId] = useState('');
  const [direction, setDirection] = useState<'all' | 'in' | 'out'>('all');
  const [status, setStatus] = useState<'all' | 'success' | 'warning' | 'error' | 'running'>('all');

  const { data: apps } = useQuery({
    queryKey: ['admin', 'apps'],
    queryFn: () => api.get<AppLite[]>('/api/apps'),
  });

  const params = useMemo(() => {
    const p: Record<string, string | number> = { page, pageSize: 15 };
    if (appId) p.appId = appId;
    if (direction !== 'all') p.direction = direction;
    if (status !== 'all') p.status = status;
    return p;
  }, [page, appId, direction, status]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'logs', params],
    queryFn: () => api.get<LogsResponse>('/api/logs', params),
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-800">Journaux ETL</h2>
        <p className="text-sm text-slate-600 mt-1">Historique des synchronisations</p>
      </div>

      <div className="flex flex-wrap gap-3 items-end bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Application
          <select
            value={appId}
            onChange={(e) => {
              setAppId(e.target.value);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2 min-w-[180px]"
          >
            <option value="">Toutes</option>
            {(apps ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.appName}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Direction
          <select
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value as typeof direction);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
          >
            <option value="all">Toutes</option>
            <option value="in">Inbound</option>
            <option value="out">Outbound</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-600">
          Statut
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as typeof status);
              setPage(1);
            }}
            className="text-sm border border-slate-200 rounded-lg px-3 py-2"
          >
            <option value="all">Tous</option>
            <option value="success">success</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
            <option value="running">running</option>
          </select>
        </label>
      </div>

      {isLoading && <div className="animate-pulse h-48 bg-slate-200 rounded-xl" />}
      {isError && (
        <div className="text-red-600 text-sm">
          Erreur.{' '}
          <button type="button" className="underline" onClick={() => refetch()}>
            Réessayer
          </button>
        </div>
      )}

      {data && (
        <>
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Horodatage</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">App</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Direction</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Statut</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Records</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Durée (ms)</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-slate-500">
                      Aucune entrée
                    </td>
                  </tr>
                ) : (
                  data.items.map((l) => (
                    <tr key={l.id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                        {new Date(l.startedAt).toLocaleString('fr-CA')}
                      </td>
                      <td className="px-3 py-2">{l.appName}</td>
                      <td className="px-3 py-2">{l.direction}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            l.status === 'success'
                              ? 'bg-emerald-100 text-emerald-800'
                              : l.status === 'error'
                                ? 'bg-red-100 text-red-800'
                                : l.status === 'running'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-amber-100 text-amber-900'
                          }`}
                        >
                          {l.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">{l.records ?? '—'}</td>
                      <td className="px-3 py-2 text-right">{l.durationMs ?? '—'}</td>
                      <td className="px-3 py-2 max-w-xs truncate text-slate-600" title={l.message ?? ''}>
                        {l.message ?? '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              Page {data.page} / {Math.max(1, data.totalPages)} — {data.total} ligne(s)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40"
              >
                Précédent
              </button>
              <button
                type="button"
                disabled={page >= data.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="px-3 py-1 rounded-lg border border-slate-200 disabled:opacity-40"
              >
                Suivant
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
